/**
 * Marketplace Constants - Single source of truth
 * Statuses, configs, and options for listings, offers, and appointments
 */
import { Clock, Check, X, RotateCcw, Ban } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════
// LISTING STATUS
// ═══════════════════════════════════════════════════════════════════════

export const LISTING_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  SOLD: 'sold',
  EXCHANGED: 'exchanged',
  EXPIRED: 'expired',
};

export const LISTING_STATUS_CONFIG = {
  [LISTING_STATUS.ACTIVE]: { label: 'Active', color: 'bg-green-500/20 text-green-400' },
  [LISTING_STATUS.PAUSED]: { label: 'En pause', color: 'bg-amber-500/20 text-amber-400' },
  [LISTING_STATUS.SOLD]: { label: 'Vendu', color: 'bg-blue-500/20 text-blue-400' },
  [LISTING_STATUS.EXCHANGED]: { label: 'Échangé', color: 'bg-purple-500/20 text-purple-400' },
  [LISTING_STATUS.EXPIRED]: { label: 'Expiré', color: 'bg-dark-hover text-secondary' },
};

export const getListingStatusConfig = (status) =>
  LISTING_STATUS_CONFIG[status] || LISTING_STATUS_CONFIG[LISTING_STATUS.ACTIVE];

// ─── Listing Type Labels ─────────────────────────────────────────────
export const LISTING_TYPE_LABELS = {
  sale: 'Vente',
  exchange: 'Échange',
  both: 'Vente/Échange',
};

// ═══════════════════════════════════════════════════════════════════════
// OFFER STATUS
// ═══════════════════════════════════════════════════════════════════════

export const OFFER_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
  COUNTERED: 'countered',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
};

export const OFFER_STATUS_CONFIG = {
  [OFFER_STATUS.PENDING]: { label: 'En attente', color: 'bg-amber-500/20 text-amber-400', icon: Clock },
  [OFFER_STATUS.ACCEPTED]: { label: 'Acceptée', color: 'bg-green-500/20 text-green-400', icon: Check },
  [OFFER_STATUS.REJECTED]: { label: 'Refusée', color: 'bg-red-500/20 text-red-400', icon: X },
  [OFFER_STATUS.COUNTERED]: { label: 'Contre-offre', color: 'bg-blue-500/20 text-blue-400', icon: RotateCcw },
  [OFFER_STATUS.EXPIRED]: { label: 'Expirée', color: 'bg-dark-hover text-muted', icon: Clock },
  [OFFER_STATUS.CANCELLED]: { label: 'Annulée', color: 'bg-dark-hover text-muted', icon: Ban },
};

export const getOfferStatusConfig = (status) =>
  OFFER_STATUS_CONFIG[status] || OFFER_STATUS_CONFIG[OFFER_STATUS.PENDING];

export const OFFER_CLOSED_STATUSES = ['accepted', 'rejected', 'expired', 'cancelled'];

// ═══════════════════════════════════════════════════════════════════════
// APPOINTMENT STATUS
// ═══════════════════════════════════════════════════════════════════════

export const APPOINTMENT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
};

export const APPOINTMENT_STATUS_CONFIG = {
  [APPOINTMENT_STATUS.PENDING]: { label: 'En attente', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  [APPOINTMENT_STATUS.CONFIRMED]: { label: 'Confirmé', bg: 'bg-green-500/20', text: 'text-green-400' },
  [APPOINTMENT_STATUS.COMPLETED]: { label: 'Terminé', bg: 'bg-gray-500/20', text: 'text-secondary' },
  [APPOINTMENT_STATUS.REJECTED]: { label: 'Refusé', bg: 'bg-red-500/20', text: 'text-red-400' },
  [APPOINTMENT_STATUS.CANCELLED]: { label: 'Annulé', bg: 'bg-dark-hover', text: 'text-secondary' },
};

export const getAppointmentStatusConfig = (status) =>
  APPOINTMENT_STATUS_CONFIG[status] || APPOINTMENT_STATUS_CONFIG[APPOINTMENT_STATUS.PENDING];

// ─── Appointment Reject Reasons ──────────────────────────────────────
export const APPOINTMENT_REJECT_REASONS = [
  { value: 'Véhicule non disponible', label: 'Véhicule non disponible' },
  { value: 'Personne au showroom à cette heure', label: 'Personne au showroom à cette heure' },
  { value: 'Créneau déjà pris', label: 'Créneau déjà pris' },
  { value: 'Véhicule vendu', label: 'Véhicule vendu' },
  { value: 'other', label: 'Autre raison...' },
];

// ═══════════════════════════════════════════════════════════════════════
// MARKETPLACE SORT & FILTER OPTIONS
// ═══════════════════════════════════════════════════════════════════════

export const MARKETPLACE_SORT_OPTIONS = [
  { value: 'newest', label: 'Récentes' },
  { value: 'price_asc', label: 'Prix ↑' },
  { value: 'price_desc', label: 'Prix ↓' },
  { value: 'mileage_asc', label: 'Km ↑' },
  { value: 'year_desc', label: 'Année ↓' },
];

export const BUDGET_PILLS = [
  { value: [0, 10000], label: '< 10k' },
  { value: [10000, 20000], label: '10-20k' },
  { value: [20000, 35000], label: '20-35k' },
  { value: [35000, 50000], label: '35-50k' },
  { value: [50000, null], label: '50k+' },
];
